import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import GroupCountIcon from "../../../../../common/onboarding/third-step/group-students/common/GroupCountIcon";
import { getFullName, isShowConfimationDialog, uniqueByKey } from "../../../../../../utils/NamesUtils";
import { PopupDismissalKeys, Student } from "../../../../../../store/onboarding/types";
import { OverlayTrigger, Popover } from "react-bootstrap";
import GroupStudentName from "./GroupStudentName";
import { showConfirmDialog } from "../../../../../../store/confirm-dialog/actions";
import { useDispatch, useSelector } from "react-redux";
import {
  GroupRecommendation,
  InterventionGroup,
  StudentGoal,
} from "../../../../../../store/onboarding/cases/types";
import { intersection } from "lodash";
import { deleteInterventionGroup } from "../../../../../../store/onboarding/cases/actions";
import { ApplicationState } from "../../../../../../store";
import useInterventionGroupAccess from "../../../../../../utils/hooks/useInterventionGroupAccess";

type OwnProps = {
  isReadonly?: boolean;
  students: Array<Student>;
  interventionGroup: InterventionGroup;
  exitedStudentsGroup?: boolean;
  onDragDropStudent: (student: Student, group: GroupRecommendation) => void;
  isShowStudentCount?: boolean;
};

type Props = OwnProps;

const MAX_DISPLAYED_NAMES_NUM = 3;

const InterventionGroupStudents: FunctionComponent<Props> = ({
  interventionGroup,
  isReadonly,
  students,
  exitedStudentsGroup,
  onDragDropStudent,
  isShowStudentCount
}) => {
  const dispatch = useDispatch();

  const [showAll, setStateShowAll] = useState<boolean>(false);

  const displayGroup = useSelector((s: ApplicationState) => s.cases.displayGroupName);

  const { isIntGrpIDsWithReadAccess } = useInterventionGroupAccess(interventionGroup.id);

  //const [prevGroupState, setPrevGroupState] = useState<any>();

//   const bothStudentGroupUpdated = useSelector<
//   ApplicationState,
//   boolean | undefined
// >((s) => s.cases.bothStudentGroupUpdated);

  // const firstUpdate = useRef(true);

  // useEffect(() => {
  //   if(prevGroupState) {
  //     let showStudentWarnArray:Student[] = 
  //           prevGroupState?.finished ? 
  //             prevGroupState.students.filter(
  //               (student:Student) => prevGroupState.student_goals.find(
  //                 (sg:StudentGoal) => (student.id == sg.student.id) && !sg.archived))
  //           : prevGroupState.students;
  //     if(!prevGroupState?.finished && bothStudentGroupUpdated && (showStudentWarnArray.length <= 1) ) {
  //       if(firstUpdate.current){
  //       dispatch(deleteInterventionGroup(prevGroupState.id!));
  //       firstUpdate.current = false;
  //         return;
  //       }

  //     }
  //   }
  // }, [bothStudentGroupUpdated])

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const filterStudents = (
    selectedInterventionGroup: InterventionGroup | undefined
  ) => {
    let studentsList = selectedInterventionGroup?.students.filter(
      (student, index) => {
        let studentGoal: StudentGoal | undefined;
        if (selectedInterventionGroup?.student_goals) {
          if(isShowStudentCount) {
            studentGoal = selectedInterventionGroup?.student_goals.find(
             (x) => x.student.id === student.id && ((displayGroup == 'archived' || isReadonly) ? x.archived : !x.archived)
           );
          } else {
            studentGoal = selectedInterventionGroup?.student_goals.find(
              (x) => x.student.id !== student.id && ((displayGroup == 'archived' || isReadonly) ? x.archived : !x.archived)
            );
          }
        }
        return !!studentGoal;
      }
    );

    studentsList = exitedStudentsGroup
      ? students
      : 
        (displayGroup == 'archived' || isReadonly) 
        ? uniqueByKey([...studentsList!, ...students], 'id')
        : intersection(studentsList!,students)//if first array empty, intersection will return empty array

    return studentsList;
  };

  const currentStudents = useMemo(() => {
    const result = filterStudents(interventionGroup);
    return result != undefined ? result : [];
  }, [students, interventionGroup]);

  const userDismissPopupDetails =
              useSelector(
                (s: ApplicationState) => s.auth.userInfo?.user_dismiss_pop_up_details || {}
              );
  const handleDrop = (event: React.DragEvent) => {
    const studentData = event.dataTransfer.getData("student");
    const prevGroupData = event.dataTransfer.getData("prevGroup");
    
    if (studentData && prevGroupData) {
      const student = JSON.parse(studentData);
      const prevGroup = JSON.parse(prevGroupData);

      if(interventionGroup.id == prevGroup.id) {
        //unable to drag and drop student in same group
        return;
      }
        
      let showStudentWarnArray:Student[] = 
          prevGroup?.finished ? 
            prevGroup.students.filter(
              (student:Student) => prevGroup.student_goals.find(
                (sg:StudentGoal) => (student.id == sg.student.id) && !sg.archived))
          : prevGroup.students;

      let isStudentWithGoal: boolean = prevGroup.student_goals && prevGroup.student_goals.some(
        (sg:StudentGoal) => (student.id == sg.student.id) && !sg.archived);  

      if (isStudentWithGoal
          && isShowConfimationDialog( 
              showStudentWarnArray.length <= 1 
              ? userDismissPopupDetails[PopupDismissalKeys.DROP_LAST_STUDENT_INTERVENTION_GROUP_CARD]
              : undefined
          )) {
        dispatch(
          showConfirmDialog({
            title: "Move Student",
            text:  <>
                  <p className="font-weight-bold text-center"> Are you sure you want to move this student?</p>
                  <p className="text-center">{!prevGroup.finished 
                    ? "Primary goals will not move to the new group with them."
                    : "Primary goals will be archived, and secondary goals will be permanently deleted. Neither will transfer to the new group."}
                  </p>
                  </>,
            showLastStudentWarning: showStudentWarnArray.length <= 1,
            isFinished: prevGroup?.finished,
            countDownTimer: showStudentWarnArray.length <= 1 ? 3 : 0,
            cancelButtonText: "Cancel",
            confirmButtonText: "Yes, Move Student",
            onConfirm: () => onDragDropStudent(student, prevGroup),
            popupKey: showStudentWarnArray.length <= 1  
                      ? PopupDismissalKeys.DROP_LAST_STUDENT_INTERVENTION_GROUP_CARD
                      : undefined
          })
        );
      } else {
        onDragDropStudent(student, prevGroup);
      }
    }
  };

  return (
    <>
    {isShowStudentCount 
    ? 
      <div className="d-flex flex-column mr-3">
        <GroupCountIcon svgSize={80} studentsNum={currentStudents.length} />
        {!isReadonly && !isIntGrpIDsWithReadAccess && (
          <small
            className="font-weight-bold details-link"
            style={{ marginBottom: "-10px" }}
          >
            Click for details
          </small>
        )}
      </div>
    : ''}
    {currentStudents.length
    ? 
      <div
        className="interventionStudentsContainer flex-1"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {(showAll
          ? currentStudents
          : currentStudents.slice(0, MAX_DISPLAYED_NAMES_NUM)
        ).map((student) => (
          <GroupStudentName
            key={student.id}
            student={student}
            interventionGroup={interventionGroup}
            isReadonly={isReadonly}
            isIntGrpIDsWithReadAccess={isIntGrpIDsWithReadAccess}
          />
        ))}
        {!showAll && currentStudents.length > MAX_DISPLAYED_NAMES_NUM && (
          <OverlayTrigger
            overlay={
              <Popover id={"tooltip"}>
                <Popover.Content>
                  {currentStudents
                    .slice(MAX_DISPLAYED_NAMES_NUM)
                    .map(getFullName)
                    .join(", ")}
                </Popover.Content>
              </Popover>
            }
          >
            <div
              className="moreStudentsContainer"
              onClick={(e) => {
                e.stopPropagation();
                setStateShowAll(true);
              }}
            >
              +{currentStudents.length - MAX_DISPLAYED_NAMES_NUM} more
            </div>
          </OverlayTrigger>
        )}
      </div>
    : 
    (<i>No students</i>)}
    </>
  );
};

export default InterventionGroupStudents;
